import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ALLERGEN_LABELS, DIET_LABELS, type HouseholdPersonDto } from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import { EmptyState } from '../../components/EmptyState';
import { Field } from '../../components/Field';
import { UsersIcon } from '../../components/icons';
import { MiniChips } from '../../components/profiles/ChipToggles';
import { PersonProfileSheet } from '../../components/profiles/PersonProfileSheet';
import { Section } from '../../components/Section';
import { api, queryKeys } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { messageForError } from '../../lib/errors';
import { canEditPerson } from '../../lib/profiles';
import { recipeKeys } from '../recipes/queries';

const NAME_MAX = 120;

function nameError(name: string): string | null {
  if (name.trim() === '') return 'Enter a name.';
  if (name.trim().length > NAME_MAX) return `Keep it under ${NAME_MAX} characters.`;
  return null;
}

/**
 * Settings → Household → Family members (D30): everyone who can have a
 * recipe rating. Members with an account appear automatically; owners and
 * adults add, rename and remove people without one (young children, a
 * grandparent who never signs in).
 */
export function FamilyMembersSection({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const { auth } = useAuth();
  const role = auth.status === 'authed' ? auth.role : null;
  const [profileOf, setProfileOf] = useState<string | null>(null);
  const people = useQuery({
    queryKey: queryKeys.people,
    queryFn: () => api.households.people.list(),
  });
  const [newName, setNewName] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<HouseholdPersonDto | null>(null);

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: queryKeys.people });
    // Names and columns on recipe pages and rankings follow the people list.
    void qc.invalidateQueries({ queryKey: recipeKeys.all });
  };
  const add = useMutation({
    mutationFn: (name: string) => api.households.people.create({ name }),
    onSuccess: async () => {
      setNewName('');
      await refresh();
    },
  });
  const rename = useMutation({
    mutationFn: (v: { id: string; name: string }) =>
      api.households.people.rename(v.id, { name: v.name }),
    onSuccess: async () => {
      setEditing(null);
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.households.people.remove(id),
    onSuccess: async () => {
      setRemoving(null);
      await refresh();
    },
  });

  function onAdd(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const err = nameError(newName);
    setAddError(err);
    if (!err) add.mutate(newName.trim());
  }

  function onRename(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!editing) return;
    const err = nameError(editing.name);
    setEditError(err);
    if (!err) rename.mutate({ id: editing.id, name: editing.name.trim() });
  }

  const list = people.data?.people ?? [];
  const withoutAccount = list.filter((p) => !p.hasAccount).length;
  const profilePerson = profileOf ? list.find((p) => p.id === profileOf) : undefined;

  return (
    <Section
      id="family-members"
      title="Family members"
      accent="orange"
      icon={<UsersIcon className="size-5" />}
      description="Everyone here can have their own ratings, allergies, diets and dislikes. Allergies and diets are kept out of the meals they eat."
    >
      {people.isError ? <Alert tone="error">{messageForError(people.error)}</Alert> : null}
      {rename.isError ? <Alert tone="error">{messageForError(rename.error)}</Alert> : null}

      {people.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : (
        <ul className="flex flex-col divide-y divide-line" aria-label="Family members">
          {list.map((p) =>
            editing?.id === p.id ? (
              <li key={p.id} className="py-2">
                <form onSubmit={onRename} noValidate className="flex flex-col gap-2">
                  <Field
                    id={`person-name-${p.id}`}
                    label={`New name for ${p.name}`}
                    value={editing.name}
                    maxLength={NAME_MAX}
                    autoFocus
                    error={editError ?? undefined}
                    onChange={(e) => setEditing({ id: p.id, name: e.target.value })}
                  />
                  <div className="flex gap-2">
                    <Button type="submit" size="sm" block={false} disabled={rename.isPending}>
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      block={false}
                      onClick={() => {
                        setEditing(null);
                        setEditError(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </li>
            ) : (
              <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <p className="flex min-w-0 flex-wrap items-center gap-1.5 font-medium">
                    <span className="truncate">{p.name}</span>
                    {p.isMe ? <span className="text-muted">(you)</span> : null}
                    {p.hasAccount ? (
                      <span className="rounded-full bg-accent-soft px-2 py-0.5 text-xs font-medium text-brand">
                        Account
                      </span>
                    ) : (
                      <span className="rounded-full bg-orange-soft px-2 py-0.5 text-xs font-medium text-orange-ink">
                        No account
                      </span>
                    )}
                  </p>
                  <MiniChips
                    label={`${p.name}’s allergies`}
                    tone="danger"
                    items={p.allergens.map((a) => ALLERGEN_LABELS[a])}
                  />
                  <MiniChips
                    label={`${p.name}’s diets`}
                    tone="brand"
                    items={p.diets.map((d) => DIET_LABELS[d])}
                  />
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    block={false}
                    aria-label={`${p.name}’s profile`}
                    onClick={() => setProfileOf(p.id)}
                  >
                    Profile
                  </Button>
                  {canManage && !p.hasAccount ? (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        block={false}
                        aria-label={`Rename ${p.name}`}
                        onClick={() => {
                          setEditError(null);
                          setEditing({ id: p.id, name: p.name });
                        }}
                      >
                        Rename
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        block={false}
                        aria-label={`Remove ${p.name}`}
                        onClick={() => setRemoving(p)}
                      >
                        Remove
                      </Button>
                    </>
                  ) : null}
                </div>
              </li>
            ),
          )}
        </ul>
      )}

      {!people.isPending && withoutAccount === 0 ? (
        <EmptyState
          compact
          accent="orange"
          icon={<UsersIcon className="size-full text-orange-ink" />}
          title="Kids without an account?"
        >
          {canManage
            ? 'Add them below so their likes and dislikes count when you plan the week.'
            : 'An owner or adult can add them here so their ratings count too.'}
        </EmptyState>
      ) : null}

      {canManage ? (
        <form onSubmit={onAdd} noValidate className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Field
              id="new-person-name"
              label="Add someone without an account"
              placeholder="e.g. Emma"
              value={newName}
              maxLength={NAME_MAX}
              error={addError ?? undefined}
              onChange={(e) => setNewName(e.target.value)}
            />
          </div>
          <Button type="submit" variant="secondary" block={false} disabled={add.isPending}>
            Add person
          </Button>
        </form>
      ) : null}
      {add.isError ? <Alert tone="error">{messageForError(add.error)}</Alert> : null}

      {profilePerson ? (
        <PersonProfileSheet
          key={profilePerson.id}
          person={profilePerson}
          canEdit={canEditPerson(role, profilePerson)}
          onClose={() => setProfileOf(null)}
        />
      ) : null}

      <Dialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={removing ? `Remove ${removing.name}?` : 'Remove person'}
      >
        <p className="text-sm text-ink">
          Every recipe rating given for {removing?.name ?? 'them'} will be deleted too. This
          can&apos;t be undone.
        </p>
        {remove.isError ? <Alert tone="error">{messageForError(remove.error)}</Alert> : null}
        <div className="flex flex-col gap-2 sm:flex-row-reverse">
          <Button
            variant="danger"
            block={false}
            disabled={remove.isPending}
            onClick={() => removing && remove.mutate(removing.id)}
          >
            Remove and delete ratings
          </Button>
          <Button variant="ghost" block={false} onClick={() => setRemoving(null)}>
            Cancel
          </Button>
        </div>
      </Dialog>
    </Section>
  );
}
